// Shop.jsx — product card, shop grid, PDP, cart drawer (warm editorial)
import React, { useState } from "react";
import { Display, Eyebrow, Reveal, Btn, Icon, Loader } from "./Primitives";

const WRAP = { maxWidth: "var(--maxw)", margin: "0 auto", padding: "0 clamp(20px,4vw,56px)" };
const tint = (hex, a = "22") => `${hex}${a}`;

export function ProductCard({ p, onOpen, onAdd }) {
  return (
    <div className="k-prod-card" data-testid={`shop-card-${p.id}`} data-hover onClick={() => onOpen(p)}>
      <div className="k-prod-media">
        <img src={p.image} alt={`Klaren ${p.name} in a ${p.category.toLowerCase()}`} loading="lazy" />
        <span style={{ position: "absolute", top: 16, left: 16, background: "rgba(246,241,232,.82)", backdropFilter: "blur(6px)", borderRadius: 999, padding: "5px 12px", fontFamily: "var(--font-sans)", fontSize: 10.5, fontWeight: 600, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--ink-2)" }}>{p.category}</span>
      </div>
      <div style={{ padding: "22px 24px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: p.accent, flex: "none" }} />
            <Display size={26} style={{ fontWeight: 500 }}>{p.name}</Display>
          </div>
          <span style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 500 }}>${p.price}</span>
        </div>
        <p style={{ color: "var(--ink-2)", fontSize: 14.5, lineHeight: 1.55, margin: "10px 0 18px" }}>{p.short}</p>
        <Btn variant="outline" size="sm" full onClick={(e) => { e.stopPropagation(); onAdd(p.id); }} testId={`shop-add-${p.id}`} icon="arrow-right">Add to bag</Btn>
      </div>
    </div>
  );
}

export function ShopPage({ products, loading, onOpen, onAdd }) {
  return (
    <main style={{ paddingTop: "clamp(120px,16vh,170px)", paddingBottom: 110, background: "var(--paper)" }}>
      <div style={{ ...WRAP }}>
        <Reveal>
          <Eyebrow>The range</Eyebrow>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 18, marginTop: 16 }}>
            <Display size="clamp(38px,5.6vw,72px)" style={{ maxWidth: 760, fontWeight: 400 }}>Three daily essentials. <span className="k-serif-i" style={{ color: "var(--ink-2)" }}>Made gentler.</span></Display>
            <span className="k-eye" style={{ paddingBottom: 8 }}>003 · Refillable</span>
          </div>
        </Reveal>
        {loading ? <Loader label="Gathering the range" /> : (
          <div className="k-range-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(18px,2.2vw,30px)", marginTop: "clamp(40px,6vh,64px)" }}>
            {products.map((p, i) => <Reveal key={p.id} delay={0.07 * i}><ProductCard p={p} onOpen={onOpen} onAdd={onAdd} /></Reveal>)}
          </div>
        )}
      </div>
    </main>
  );
}

function Accordion({ title, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--line)" }}>
      <button data-hover data-testid={testId} onClick={() => setOpen(!open)} style={{ width: "100%", background: "none", border: "none", color: "var(--ink)", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 0", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600 }}>
        {title}<Icon name={open ? "minus" : "plus"} size={17} color="var(--ink-2)" />
      </button>
      <div style={{ maxHeight: open ? 360 : 0, overflow: "hidden", transition: "max-height .5s var(--ease)" }}>
        <p style={{ color: "var(--ink-2)", fontSize: 14.5, lineHeight: 1.7, margin: "0 0 22px", maxWidth: 480 }}>{children}</p>
      </div>
    </div>
  );
}

export function ProductPage({ p, onAdd, onBack }) {
  const [size, setSize] = useState(p.sizes[0]);
  return (
    <main style={{ paddingTop: "clamp(104px,13vh,140px)", paddingBottom: 110, background: "var(--paper)" }}>
      <div style={{ ...WRAP }}>
        <button data-hover data-testid="pdp-back" onClick={onBack} style={{ background: "none", border: "none", color: "var(--ink-2)", display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, marginBottom: 28 }}><Icon name="arrow-left" size={16} /> Back to the range</button>
        <div className="k-pdp" style={{ display: "grid", gridTemplateColumns: "1.02fr 1fr", gap: "clamp(28px,4vw,64px)", alignItems: "start" }}>
          <Reveal>
            <div className="k-stage" style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)", background: "var(--sand)", aspectRatio: "1/1" }}>
              <img className="k-stage-img" src={p.image} alt={`Klaren ${p.name}`} />
              <span style={{ position: "absolute", top: 18, left: 18, background: "rgba(246,241,232,.85)", backdropFilter: "blur(6px)", borderRadius: 999, padding: "6px 14px", fontFamily: "var(--font-sans)", fontSize: 11, fontWeight: 600, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--ink-2)" }}>{p.category}</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: p.accent }} />
                <Eyebrow>{p.scent}</Eyebrow>
              </div>
              <Display size="clamp(40px,5vw,68px)" style={{ margin: "14px 0 0", fontWeight: 400 }}>{p.name}</Display>
              <p style={{ color: "var(--ink-2)", fontSize: 17, lineHeight: 1.65, margin: "18px 0 0", maxWidth: 460 }}>{p.long}</p>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "26px 0" }}>
                {p.specs.map((s) => <span key={s} style={{ fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 500, letterSpacing: ".02em", color: "var(--ink-2)", background: tint(p.accent, "1c"), border: `1px solid ${tint(p.accent, "40")}`, borderRadius: 999, padding: "7px 14px" }}>{s}</span>)}
              </div>

              <div className="k-eye" style={{ marginBottom: 11 }}>Size</div>
              <div style={{ display: "flex", gap: 10, marginBottom: 28, flexWrap: "wrap" }}>
                {p.sizes.map((s, i) => <button key={s} data-hover data-testid={`pdp-size-${i}`} onClick={() => setSize(s)} style={{ background: size === s ? "var(--ink)" : "transparent", border: `1px solid ${size === s ? "var(--ink)" : "var(--line)"}`, color: size === s ? "var(--paper)" : "var(--ink-2)", borderRadius: 999, padding: "10px 18px", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, transition: "all .3s var(--ease)" }}>{s}</button>)}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "24px 0", borderTop: "1px solid var(--line)" }}>
                <span style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 500 }}>${p.price}</span>
                <Btn variant="solid" size="lg" icon="arrow-right" style={{ marginLeft: "auto" }} onClick={() => onAdd(p.id)} testId="pdp-add-btn">Add to bag</Btn>
              </div>

              <div style={{ marginTop: 8 }}>
                <Accordion title="Ingredients" testId="pdp-acc-ingredients">{p.ingredients}</Accordion>
                <Accordion title="How to use" testId="pdp-acc-how">{p.how}</Accordion>
                <Accordion title="Good to know" testId="pdp-acc-good">Fragrance-free and {p.scent.toLowerCase()}. No added colour. Refillable by design — keep your bottle and top it up.</Accordion>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}

export function CartDrawer({ open, items, products, onClose, onQty, onCheckout, checkoutBusy }) {
  const list = Object.entries(items).filter(([, q]) => q > 0).map(([id, q]) => ({ ...products.find((p) => p.id === id), q })).filter((it) => it.id);
  const subtotal = list.reduce((s, it) => s + it.price * it.q, 0);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,24,20,.42)", backdropFilter: open ? "blur(3px)" : "none", opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: "opacity .4s var(--ease)", zIndex: 400 }} />
      <aside data-testid="cart-drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 430, maxWidth: "94vw", zIndex: 410, transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform .5s var(--ease)", display: "flex", flexDirection: "column", background: "var(--paper-2)", borderLeft: "1px solid var(--line)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "24px 26px", borderBottom: "1px solid var(--line)" }}>
          <Display size={26} style={{ fontWeight: 500 }}>Your bag</Display>
          <button data-hover data-testid="cart-close" onClick={onClose} style={{ background: "none", border: "none", color: "var(--ink)", cursor: "pointer", display: "flex" }}><Icon name="x" size={22} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 26px" }}>
          {list.length === 0 && <p style={{ color: "var(--ink-3)", fontSize: 14.5, marginTop: 34, lineHeight: 1.6 }}>Your bag is empty. Add a little everyday care to begin.</p>}
          {list.map((it) => (
            <div key={it.id} style={{ display: "flex", gap: 16, padding: "20px 0", borderBottom: "1px solid var(--line)" }}>
              <div style={{ width: 66, height: 80, borderRadius: 8, flex: "none", overflow: "hidden", border: "1px solid var(--line)", background: "var(--sand)" }}>
                <img src={it.image} alt={it.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ flex: 1 }}>
                <Display size={19} style={{ fontWeight: 500 }}>{it.name}</Display>
                <div className="k-eye" style={{ fontSize: 10.5, marginTop: 3 }}>{it.sizes[0]}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, border: "1px solid var(--line)", borderRadius: 999, padding: "5px 12px" }}>
                    <Icon name="minus" size={13} data-testid={`cart-minus-${it.id}`} style={{ cursor: "pointer", color: "var(--ink-2)" }} onClick={() => onQty(it.id, it.q - 1)} />
                    <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, minWidth: 12, textAlign: "center" }} data-testid={`cart-qty-${it.id}`}>{it.q}</span>
                    <Icon name="plus" size={13} data-testid={`cart-plus-${it.id}`} style={{ cursor: "pointer", color: "var(--ink-2)" }} onClick={() => onQty(it.id, it.q + 1)} />
                  </div>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 500 }}>${it.price * it.q}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "22px 26px", borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "var(--ink-2)", fontSize: 14.5 }}>Subtotal</span>
            <span style={{ fontFamily: "var(--font-display)", fontSize: 21, fontWeight: 500 }} data-testid="cart-subtotal">${subtotal}</span>
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 12.5, margin: "0 0 16px" }}>Free refill pouch over $40 · carbon-neutral delivery.</p>
          <Btn variant="solid" size="lg" icon={checkoutBusy ? undefined : "arrow-right"} full onClick={onCheckout} testId="cart-checkout-btn" style={{ opacity: (list.length && !checkoutBusy) ? 1 : 0.55, pointerEvents: (list.length && !checkoutBusy) ? "auto" : "none" }}>{checkoutBusy ? "Taking you to checkout…" : "Checkout"}</Btn>
        </div>
      </aside>
    </>
  );
}
